import { GameCore } from './core/GameCore';
import { UIRenderer } from './ui/UIRenderer';
import { Event, GameState, EndingType, GameStatus } from './core/types';
import './styles/main.css';

/**
 * 游戏应用类
 */
class GameApp {
  private gameCore: GameCore;
  private uiRenderer: UIRenderer;
  private loadingInterval: number | null = null;
  private _eventRenderTimer: number | null = null;
  private actualCurrentEventForProcessing: Event | null = null;

  /**
   * 构造函数
   */
  constructor() {
    // 创建游戏核心
    this.gameCore = new GameCore({
      startTime: 480, // 8:00 AM
      workStartTime: 540, // 9:00 AM
      totalDistance: 20,
      distanceChangeFactor: 3 // 设置距离变化因子为3
    });
    
    // 创建UI渲染器
    this.uiRenderer = new UIRenderer('app');
    
    // 设置事件监听
    this.setupEventListeners();
  }

  /**
   * 设置事件监听
   */
  private setupEventListeners(): void {
    // 游戏事件监听
    this.gameCore.addEventListener('loading-start', () => {
      this.startLoadingAnimation();
    });
    
    this.gameCore.addEventListener('loading-complete', () => {
      this.stopLoadingAnimation();
      this.uiRenderer.showStartScreen();
    });
    
    this.gameCore.addEventListener('loading-error', () => {
      this.stopLoadingAnimation();
      alert('游戏加载失败，请刷新页面重试。');
    });
    
    this.gameCore.addEventListener('game-started', () => {
      this.uiRenderer.showGameScreen();
      this.uiRenderer.initializeGameUI();
    });
    
    // 事件生成时的处理
    this.gameCore.addEventListener('event-generated', (event: Event) => {
      this.actualCurrentEventForProcessing = event;

      // 清除可能存在的事件渲染定时器
      if (this._eventRenderTimer) {
        clearTimeout(this._eventRenderTimer);
        this._eventRenderTimer = null;
      }
      
      // 直接渲染事件
      this.uiRenderer.renderEvent(event);
      this.updateUI(true);
    });
    
    this.gameCore.addEventListener('choice-made', (_result: unknown) => {
      // This listener might not be strictly necessary if all UI updates
      // are driven by gameState changes or specific results from processChoice.
      // For now, keeping it as it might be used for generic 'choice was made' logic.
    });
    
    this.gameCore.addEventListener('game-over', (data: any) => {
      this.actualCurrentEventForProcessing = null;
      this.uiRenderer.showGameOverScreen(
        data.state as GameState, 
        data.endingType as EndingType,
        data.specialEndingTitle as string | undefined,
        data.specialEndingDescription as string | undefined
      );
    });
    
    // UI事件监听
    this.uiRenderer.onChoiceSelected((choiceId: string) => {
      if (!this.actualCurrentEventForProcessing) {
        return;
      }
      
      const currentEventForChoice = this.actualCurrentEventForProcessing;
      const choiceIndex = currentEventForChoice.choices.findIndex(choice => choice.id === choiceId);

      if (choiceIndex !== -1) {
        //const choiceMade = currentEventForChoice.choices[choiceIndex];
        const result = this.gameCore.processChoice(choiceIndex);

        if (result) {
          const updatedState = this.gameCore.getGameState();
          this.uiRenderer.updateStatusBar(updatedState, result);

          if (this.gameCore.getGameStatus() === GameStatus.GAME_OVER || updatedState.distanceToWork <= 0) {
            if (this.gameCore.getGameStatus() !== GameStatus.GAME_OVER && updatedState.distanceToWork <= 0) {
              setTimeout(() => this.gameCore.endGame(), 50);
            }
          }
        }
      }
    });
    
    this.uiRenderer.onGameStart(() => {
      this.actualCurrentEventForProcessing = null;
      this.gameCore.startNewGame();
    });
    
    this.uiRenderer.onGameRestart(() => {
      this.actualCurrentEventForProcessing = null;
      this.gameCore.startNewGame();
    });
  }

  /**
   * 更新UI
   * @param skipMessageUpdate 是否跳过消息覆盖层更新（可选，默认为false）
   */
  private updateUI(skipMessageUpdate: boolean = false): void {
    const state = this.gameCore.getGameState();
    this.uiRenderer.updateStatusBar(state, undefined, skipMessageUpdate);
  }

  /**
   * 开始加载动画
   */
  private startLoadingAnimation(): void {
    let progress = 0;
    
    this.loadingInterval = window.setInterval(() => {
      progress += 5;
      
      if (progress > 100) {
        progress = 100;
        this.stopLoadingAnimation();
      }
      
      this.uiRenderer.updateLoadingProgress(progress);
    }, 100);
  }

  /**
   * 停止加载动画
   */
  private stopLoadingAnimation(): void {
    if (this.loadingInterval !== null) {
      clearInterval(this.loadingInterval);
      this.loadingInterval = null;
      this.uiRenderer.updateLoadingProgress(100);
    }
  }

  /**
   * 初始化游戏
   */
  async init(): Promise<void> {
    try {
      await this.gameCore.initialize();
    } catch (error) {
      // 初始化失败处理
    }
  }
}

// 当DOM加载完成后初始化游戏
document.addEventListener('DOMContentLoaded', () => {
  const app = new GameApp();
  app.init();
}); 